import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, ParamMap } from '@angular/router';

import { DropdownMultiselectItem } from '@bayclubs/drop-down';
import { TimeService } from '@bayclubs/utils';

import { campsenum } from '../../../../models/enums.generated';
import { CouponApiService } from '../../../../services/api/coupon-api.service';
import { CampsEnumTranslationService } from '../../../../services/camps-enum-translation.service';
import { CouponsFilter } from '../../../../models/filters/coupons-filter.model';

@Component({
    selector: 'app-coupons-search-criteria',
    templateUrl: './coupons-search-criteria.component.html',
    styleUrls: ['./coupons-search-criteria.component.scss'],
    standalone: false
})
export class CouponsSearchCriteriaComponent implements OnInit {
    @Input()
    public searchForm: UntypedFormGroup;

    public typeDropdownItems: DropdownMultiselectItem[] = [];
    public categoryDropdownItems: DropdownMultiselectItem[] = [];
    public statusDropdownItems: DropdownMultiselectItem[] = [];
    public usageTypeDropdownItems: DropdownMultiselectItem[] = [];

    @Input() public filters: CouponsFilter;

    constructor(
        private enumTranslation: CampsEnumTranslationService,
        private api: CouponApiService
    ) {
    }

    ngOnInit() {
        this.createTypeItems(this.filters.types);
        this.createStatusItems(this.filters.statuses);
        this.createCategoryItems(this.filters.categories);
        this.createUsageTypeItems(this.filters.usageTypes);
    }

    public showCodeClear() {
        return this.searchForm.controls['code'].value;
    }

    public onCodeClear() {
        this.searchForm.controls['code'].setValue(null);
    }

    public onStatusesChanged(data: Array<DropdownMultiselectItem>) {
        const statuses = data.map(x => x.id);
        this.searchForm.controls['statuses'].setValue(statuses);
    }

    public onCategoryChanged(data: Array<DropdownMultiselectItem>) {
        const categories = data.map(x => x.id);
        this.searchForm.controls['categories'].setValue(categories);
    }

    public onTypesChanged(data: Array<DropdownMultiselectItem>) {
        const types = data.map(x => x.id);
        this.searchForm.controls['types'].setValue(types);
    }

    public onUsageTypesChanged(data: Array<DropdownMultiselectItem>) {
        const types = data.map(x => x.id);
        this.searchForm.controls['usageTypes'].setValue(types);
    }

    private createTypeItems(preselectedTypes: campsenum.CouponType[]) {
        const allSelected = !preselectedTypes || preselectedTypes.length === 0;

        this.typeDropdownItems.push(
            this.createDropdownItem(
                campsenum.CouponType.Amount,
                this.enumTranslation.couponType(campsenum.CouponType.Amount),
                allSelected || preselectedTypes.some(x => +x === campsenum.CouponType.Amount),
                this.enumTranslation.couponType(campsenum.CouponType.Amount)
            )
        );
        this.typeDropdownItems.push(
            this.createDropdownItem(
                campsenum.CouponType.FreeWeeks,
                this.enumTranslation.couponType(campsenum.CouponType.FreeWeeks),
                allSelected || preselectedTypes.some(x => +x === campsenum.CouponType.FreeWeeks),
                this.enumTranslation.couponType(campsenum.CouponType.FreeWeeks)
            )
        );
        this.typeDropdownItems.push(
            this.createDropdownItem(
                campsenum.CouponType.Percentage,
                this.enumTranslation.couponType(campsenum.CouponType.Percentage),
                allSelected || preselectedTypes.some(x => +x === campsenum.CouponType.Percentage),
                this.enumTranslation.couponType(campsenum.CouponType.Percentage)
            )
        );
    }

    private createCategoryItems(preselectedCategories: string[]) {
        this.api.context().subscribe(result => {
            const allSelected = !preselectedCategories || preselectedCategories.length === 0;
            if (result.isSuccess) {
                this.categoryDropdownItems = [];
                result.value.categories.forEach(c => {
                    this.categoryDropdownItems.push(
                        this.createDropdownItem(c.id, c.id, allSelected || preselectedCategories.some(x => x === c.id), c.name)
                    );
                });
            }
        });
    }

    private createStatusItems(preselectedStatuses: campsenum.CouponStatus[]) {
        const allSelected = !preselectedStatuses || preselectedStatuses.length === 0;

        this.statusDropdownItems.push(
            this.createDropdownItem(
                campsenum.CouponStatus.Created,
                this.enumTranslation.couponStatus(campsenum.CouponStatus.Created),
                allSelected || preselectedStatuses.some(x => x === campsenum.CouponStatus.Created),
                this.enumTranslation.couponStatus(campsenum.CouponStatus.Created)
            )
        );
        this.statusDropdownItems.push(
            this.createDropdownItem(
                campsenum.CouponStatus.Deactivated,
                this.enumTranslation.couponStatus(campsenum.CouponStatus.Deactivated),
                allSelected || preselectedStatuses.some(x => +x === campsenum.CouponStatus.Deactivated),
                this.enumTranslation.couponStatus(campsenum.CouponStatus.Deactivated)
            )
        );
        this.statusDropdownItems.push(
            this.createDropdownItem(
                campsenum.CouponStatus.Used,
                this.enumTranslation.couponStatus(campsenum.CouponStatus.Used),
                allSelected || preselectedStatuses.some(x => +x === campsenum.CouponStatus.Used),
                this.enumTranslation.couponStatus(campsenum.CouponStatus.Used)
            )
        );
        this.statusDropdownItems.push(
            this.createDropdownItem(
                campsenum.CouponStatus.Expired,
                this.enumTranslation.couponStatus(campsenum.CouponStatus.Expired),
                allSelected || preselectedStatuses.some(x => +x === campsenum.CouponStatus.Expired),
                this.enumTranslation.couponStatus(campsenum.CouponStatus.Expired)
            )
        );
    }

    private createUsageTypeItems(preselectedUsageTypes: campsenum.CouponUsageType[]) {
        const allSelected = !preselectedUsageTypes || preselectedUsageTypes.length === 0;

        this.usageTypeDropdownItems.push(
            this.createDropdownItem(
                campsenum.CouponUsageType.Single,
                this.enumTranslation.couponUsageType(campsenum.CouponUsageType.Single),
                allSelected || preselectedUsageTypes.some(x => +x === campsenum.CouponUsageType.Single),
                this.enumTranslation.couponUsageType(campsenum.CouponUsageType.Single)
            )
        );
        this.usageTypeDropdownItems.push(
            this.createDropdownItem(
                campsenum.CouponUsageType.Multiple,
                this.enumTranslation.couponUsageType(campsenum.CouponUsageType.Multiple),
                allSelected || preselectedUsageTypes.some(x => +x === campsenum.CouponUsageType.Multiple),
                this.enumTranslation.couponUsageType(campsenum.CouponUsageType.Multiple)
            )
        );
    }

    private createDropdownItem(id, value, isSelected: boolean = false, displayValue: string = null): DropdownMultiselectItem {
        return <DropdownMultiselectItem>{
            id: id,
            value: value,
            selected: isSelected,
            displayName: displayValue
        };
    }
}
